import { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseMultiPageModel, type TextElement, TextStyle } from '@bynder-studio/render-core';
import { defaultColor } from '@bynder-studio/misc';
import useTextSelection from 'packages/hooks/useTextSelection';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import { useIsOutlineColorEnabled } from '../Outline/useIsOutlineColorEnabled';
import useOverrides from './useOverrides';
import { collectElementStyleIds, textStyleToRunStyle } from './utils';

const withDefaultOutline = (style: TextStyle): TextStyle =>
    style.stroke ? { ...style, stroke: { ...style.stroke, strokeColor: defaultColor, punchOut: false } } : style;

export function useTextStyles(creativeModel: BaseMultiPageModel, selectedElement: TextElement) {
    const { id } = selectedElement ?? {};
    const [searchValue, setSearchValue] = useState('');
    const searchTerm = searchValue.trim().toLowerCase();
    const textSelection = useTextSelection();
    const [styleId, isMixed, mixedValue] = textSelection.styleIdData || ['', false, ''];
    const textStyles = creativeModel.getTextStyles();
    const item = textStyles.getStyle(styleId);
    const styleIds = (textSelection.styleId || []).filter((id) => !!id) as string[];
    const { overrides, getStylesWithOverrides } = useOverrides(creativeModel);
    const forceUpdate = useForceUpdate();
    const elementStyleIds = collectElementStyleIds(selectedElement);
    const isOutlineColorEnabled = useIsOutlineColorEnabled();

    const isAllowedToCreateTextStyle = useMemo(
        () =>
            Object.entries(textSelection).reduce((acc, [key, value]) => {
                if (key.endsWith('Data')) {
                    return acc;
                }

                if (key.startsWith('color')) {
                    return acc;
                }

                return acc && !value[1];
            }, true),
        [textSelection],
    );

    const applyStyle = useCallback<(styleId: string | null) => void>(
        (styleId) => {
            const param = {
                updateTextSettingForSelection: {
                    settings: {
                        styleId,
                    },
                },
            };

            creativeModel.updateElement(id, param);
        },
        [id, creativeModel],
    );

    const createTextStyle = useCallback<(name: string) => void>(
        (name) => {
            const [currentStroke, isStrokeMixed] = textSelection.strokeData;
            const styleId = selectedElement.createTextStyle(name, { forceDefaultOutline: isOutlineColorEnabled });

            if (styleId) {
                applyStyle(styleId);

                if (isOutlineColorEnabled && currentStroke && !isStrokeMixed) {
                    creativeModel.updateElement(id, {
                        updateTextSettingForSelection: {
                            settings: { stroke: currentStroke },
                        },
                    });
                }
            }
        },
        [selectedElement, applyStyle, isOutlineColorEnabled, textSelection, creativeModel, id],
    );

    const detachTextStyle = useCallback(() => {
        applyStyle(null);
    }, [applyStyle]);

    const renameTextStyle = useCallback<(name: string) => void>(
        (name: string) => {
            const renamedItem = { ...item, name };
            textStyles.updateStyle(renamedItem);
        },
        [textStyles, item],
    );

    const deleteTextStyle = useCallback(() => {
        styleIds.forEach((styleId) => {
            textStyles.deleteStyle(styleId);
        });
    }, [textStyles, styleIds]);

    const updateStyleWithOverrides = useCallback(() => {
        const styles = getStylesWithOverrides();

        // Pushing the selection's current overrides into the saved style(s) can otherwise bring
        // back a non-black strokeColor or punchOut: true the selection has, which would then get
        // picked up by every other element that style is applied to.
        textStyles.updateStyles(isOutlineColorEnabled ? styles.map(withDefaultOutline) : styles);
    }, [styleIds, textStyles, item, getStylesWithOverrides, isOutlineColorEnabled]);

    const resetAllOverrides = useCallback(() => {
        if (styleIds.length > 1) {
            const styles = textSelection
                .getRawStyles()
                .styleId.map((styleId) => (styleId ? textStyleToRunStyle(textStyles.getStyle(styleId)) : {}));
            const param = { updateTextSettingsByEachRun: { styles } };
            creativeModel.updateElement(id, param);
        } else {
            applyStyle(item.uuid);
        }
    }, [item, applyStyle, styleIds, textSelection, textStyles]);

    const allStyleList = textStyles.list.filter((item) => item.name.toLowerCase().includes(searchTerm));
    const elementStyleList = allStyleList.filter((item) => elementStyleIds.has(item.uuid));

    const handleSearchChange = useCallback((value) => {
        setSearchValue(value);
    }, []);

    const handleTextStyleSelect = useCallback<(item: TextStyle) => void>(
        (item: TextStyle) => {
            applyStyle(item.uuid);
        },
        [applyStyle],
    );

    useEffect(() => {
        creativeModel.eventEmitter.on('textStylesChanged', forceUpdate);

        return () => {
            creativeModel.eventEmitter.off('textStylesChanged', forceUpdate);
        };
    }, [creativeModel, forceUpdate]);

    return {
        textStyles,
        item,
        hasStyleList: textStyles.list.length > 0,
        stylesWithOverrides: getStylesWithOverrides().length,
        elementStyleList,
        allStyleList,
        searchValue,
        handleSearchChange,
        isAllowedToCreateTextStyle,
        createTextStyle,
        deleteTextStyle,
        detachTextStyle,
        renameTextStyle,
        updateStyleWithOverrides,
        resetAllOverrides,
        handleTextStyleSelect,
        styleIds,
        isMixed,
        mixedValue,
        overrides,
    };
}
